import { environment } from 'shared/environments/environment';
import { MultipartDemuxer } from './multipart-demuxer';
import { SpeechMarkup } from './speech-markup';
import { StreamError, streamErrorFromBody } from './stream-error';

export interface PartStreamHeaders {
  // Total decoded audio bytes. Present on cached parts always; on fresh synthesis only for
  // short (≤12s) parts, so absence does NOT mean the stream is unbounded — just unknown.
  audioContentLength: number | null;
  // kbps; absent on cached parts and Google voices. 48 is the server's own fallback.
  bitrateKbps: number;
  // The preset actually used (the server substitutes deleted/ineligible presets).
  voicePresetId: string | null;
}

export interface PartStreamCallbacks {
  onHeaders(headers: PartStreamHeaders): void;
  onAudio(chunk: Uint8Array): void;
  onMarkup(markups: SpeechMarkup[]): void;
  // truncated: the stream ended cleanly but delivered measurably less audio than
  // audio-content-length promised — an upstream TTS kill, not a success (IOS-2076).
  onDone(result: { audioBytes: number; truncated: boolean }): void;
  onError(error: StreamError): void;
}

export interface PartStreamRequest {
  itemId: string;
  // 0-based; the wire's `part` query param is 1-based.
  partIndex: number;
  voicePresetId: string | null;
  // Raw Firebase ID token — the proxy verifies the whole header value, no 'Bearer '.
  token: string;
}

const DEFAULT_BITRATE_KBPS = 48;
const TRUNCATION_TOLERANCE_SECONDS = 2;

// Only connect-phase failures retry; a connection lost mid-stream is almost always the
// upstream TTS being killed, and retrying it caused request storms on iOS (IOS-2059).
const MAX_CONNECT_RETRIES = 3;

// Downloads one part's multipart stream and demuxes it. The whole lifetime is one plain
// GET from byte 0 — the endpoint has no Range support; seeking is the caller's problem
// over the bytes it accumulated.
export class PartStream {
  private readonly abortController = new AbortController();
  private stopped = false;

  constructor(
    private readonly request: PartStreamRequest,
    private readonly callbacks: PartStreamCallbacks
  ) {}

  start(): void {
    void this.run();
  }

  stop(): void {
    this.stopped = true;
    this.abortController.abort();
  }

  private async run(): Promise<void> {
    let response: Response;

    try {
      response = await this.connectWithRetries();
    } catch (error) {
      if (!this.stopped) {
        this.callbacks.onError(
          error instanceof StreamError ? error : new StreamError('NETWORK', String(error))
        );
      }
      return;
    }

    if (this.stopped) {
      return;
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');

      // stop() during the body read aborts it into the catch above — a stopped stream
      // must not surface an error for a part nobody is playing anymore.
      if (this.stopped) {
        return;
      }

      this.callbacks.onError(streamErrorFromBody(response.status, body));
      return;
    }

    const headers = readPartHeaders(response.headers);
    this.callbacks.onHeaders(headers);

    if (!response.body) {
      this.callbacks.onError(new StreamError('UNKNOWN', 'Stream response has no body'));
      return;
    }

    const demuxer = new MultipartDemuxer();
    const reader = response.body.getReader();
    let audioBytes = 0;

    const emit = (audio: Uint8Array[], markups: SpeechMarkup[]): void => {
      for (const chunk of audio) {
        audioBytes += chunk.byteLength;
        this.callbacks.onAudio(chunk);
      }
      if (markups.length) {
        this.callbacks.onMarkup(markups);
      }
    };

    try {
      for (;;) {
        const { done, value } = await reader.read();

        if (this.stopped) {
          return;
        }

        if (done) {
          break;
        }

        const result = demuxer.push(value);
        emit(result.audio, result.markups);
      }
    } catch (error) {
      if (!this.stopped) {
        this.callbacks.onError(new StreamError('NETWORK', String(error)));
      }
      return;
    }

    const tail = demuxer.flush();
    emit(tail.audio, tail.markups);

    this.callbacks.onDone({
      audioBytes,
      truncated: isTruncated(audioBytes, headers),
    });
  }

  private async connectWithRetries(): Promise<Response> {
    let lastError: unknown;

    // The initial attempt plus MAX_CONNECT_RETRIES retries, backing off 1s/2s/4s BEFORE
    // each retry (iOS curve) — no pointless sleep after the final failure.
    for (let attempt = 0; attempt <= MAX_CONNECT_RETRIES; attempt++) {
      if (attempt > 0) {
        await delay(2 ** (attempt - 1) * 1000, this.abortController.signal);
      }

      try {
        return await fetch(this.buildUrl(), {
          headers: {
            Authorization: this.request.token,
            'client-version': environment.appVersion,
          },
          signal: this.abortController.signal,
        });
      } catch (error) {
        if (this.stopped) {
          throw error;
        }

        lastError = error;
      }
    }

    throw new StreamError('NETWORK', String(lastError));
  }

  private buildUrl(): string {
    const params = new URLSearchParams({
      itemId: this.request.itemId,
      part: String(this.request.partIndex + 1),
    });

    if (this.request.voicePresetId) {
      params.set('voicePresetId', this.request.voicePresetId);
    }

    return `${environment.serviceUrl}/stream-with-markup?${params.toString()}`;
  }
}

function readPartHeaders(headers: Headers): PartStreamHeaders {
  const contentLength = Number(headers.get('audio-content-length'));
  const bitrate = Number(headers.get('audio-bitrate'));

  return {
    audioContentLength: Number.isFinite(contentLength) && contentLength > 0 ? contentLength : null,
    bitrateKbps: Number.isFinite(bitrate) && bitrate > 0 ? bitrate : DEFAULT_BITRATE_KBPS,
    voicePresetId: headers.get('audio-voice-preset-id'),
  };
}

function isTruncated(audioBytes: number, headers: PartStreamHeaders): boolean {
  if (headers.audioContentLength === null) {
    return false;
  }

  const toleranceBytes = (TRUNCATION_TOLERANCE_SECONDS * headers.bitrateKbps * 1000) / 8;

  return audioBytes < headers.audioContentLength - toleranceBytes;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}
